import { Box, Button, Grid, Modal, RemoveScroll, ScrollArea, Stack } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { IOrder, IOrderPaymentLines } from '../../interfaces/Order';
import LoaderOverlay from '../../components/LoaderOverlay/LoaderOverlay';
import { useGetCashierboxes } from '../../hooks/cashierboxes/queries';
import { useForm } from '@mantine/form';
import { requiredFieldFormValidator } from '../../utils/validators/formValidators';
import { showNotification } from '../../utils/Notification/Notification';
import { memo, useEffect, useMemo, useState } from 'react';
import { useCreatePaymentLine } from '../../hooks/orders/mutations';
import { useSelector } from 'react-redux';
import { ICashierBox } from '../../interfaces/Store';
import { useGetStoreById } from '../../hooks/stores/queries';
import { autoOpenCashDrawerForPayments } from '../../utils/cashDrawer';
import { PaymentMethodsEnum } from '../../enum/PaymentMethodsEnum';
import {
  calculateCashChange,
  calculateRemainingBalance
} from '../../utils/payments/paymentAmounts';
import PaymentCaptureFields from '../Payments/PaymentCaptureFields';

export default memo(function OrderAddPaymentLineModal({
  opened,
  close,
  title,
  order,
  onAddPaymentLine,
  onPaymentPendingChange,
  pinUser
}: {
  opened: boolean;
  close: () => void;
  title: string;
  order: IOrder | null;
  onAddPaymentLine: () => void;
  onPaymentPendingChange?: (pending: boolean) => void;
  pinUser: {
    _id: string;
    name: string;
    cashierBox: string;
    permission: any;
  } | null;
}) {
  if (!opened) return;
  const { t } = useTranslation(['OrdersPage', 'FieldName', 'CommonButtons', 'Notifications']);
  const searchParams = new URLSearchParams(location.search);
  const searchParamStoreId = searchParams.get('storeId') || '';
  const selectedStoreId = order?.createdIn?._id || searchParamStoreId;
  const { isLoading: isGetCashierboxesLoading, data: cashierboxes } = useGetCashierboxes(
    !!order?.createdIn?._id || !!searchParamStoreId,
    order?.createdIn?._id || searchParamStoreId
  );
  const { data: store } = useGetStoreById(!!selectedStoreId, selectedStoreId);
  const { mutateAsync: createAsync, isPending: isCreationPending } = useCreatePaymentLine();
  const { user } = useSelector((state: any) => state.user);
  const [cashReceivedAmount, setCashReceivedAmount] = useState<number | ''>('');

  const getInitialCashierBox = (cashierboxes: ICashierBox[] | undefined) => {
    if (!cashierboxes) return '';
    if (cashierboxes.length === 0) return '';
    if (pinUser?.cashierBox) {
      //GET THE CASHIERBOX FROM THE PIN USER
      const cashierBox = cashierboxes.find((cashierbox) => cashierbox._id === pinUser.cashierBox);
      //if no cashierbox found, return the first one
      return cashierBox?._id || cashierboxes[0]?._id;
    }
    //USUALLY STAFF USER FALLS TO THIS
    if (typeof user.cashierBox === 'string' && user.cashierBox) {
      // WE FIND THE CASHIERBOX FROM THE USER IN CASE IS IN ANOTHER STORE AND CASHIERBOX ASSIGNED IS FROM OTHER STORE
      const cashierBox = cashierboxes.find((cashierbox) => cashierbox._id === user.cashierBox);
      return cashierBox?._id || (cashierboxes || [])[0]?._id;
    }
    //USUALLY COMPANY ADMIN FALLS TO THIS
    return (cashierboxes || [])[0]?._id;
  };

  const initialCashierBox = getInitialCashierBox(cashierboxes);
  const amountDue = useMemo(() => {
    if (!order) return 0;

    const paidAmount =
      order?.paymentLines?.reduce((prev: number, line: IOrderPaymentLines) => {
        return prev + Number(line.amountPaid || 0);
      }, 0) || 0;

    return Math.max(0, Number((Number(order.total || 0) - paidAmount).toFixed(2)));
  }, [order]);

  const form = useForm({
    mode: 'controlled',
    initialValues: {
      amountPaid: amountDue,
      paymentMethod: PaymentMethodsEnum.Card,
      cashierBox: initialCashierBox
    },
    validate: {
      amountPaid: (value) => {
        const requiredValidatorRes = requiredFieldFormValidator(value);
        if (requiredValidatorRes) return requiredValidatorRes;
        const numericValue = Number(value || 0);

        if (numericValue <= 0) {
          return 'El monto a aplicar debe ser mayor a 0.';
        }

        if (numericValue > amountDue) {
          return 'El monto a aplicar no puede ser mayor al total pendiente.';
        }
      },
      paymentMethod: (value) => {
        const requiredValidatorRes = requiredFieldFormValidator(value);
        if (requiredValidatorRes) return requiredValidatorRes;
      },
      cashierBox: (value) => {
        const requiredValidatorRes = requiredFieldFormValidator(value);
        if (requiredValidatorRes) return requiredValidatorRes;
      }
    }
  });

  type FormValues = typeof form.values;
  const amountToApply = Number(form.values.amountPaid || 0);
  const isCashPayment = form.values.paymentMethod === PaymentMethodsEnum.Cash;
  const remainingBalance = calculateRemainingBalance(amountDue, amountToApply);
  const changeAmount = calculateCashChange(amountToApply, cashReceivedAmount);

  const paymentMethodOptions = useMemo(() => {
    const methods = [PaymentMethodsEnum.Card, PaymentMethodsEnum.Cash, PaymentMethodsEnum.Transfer];

    return methods.map((method) => ({
      value: method,
      label: t(`FieldName:paymentMethods.${method}`)
    }));
  }, [t]);

  const handleSubmit = async (values: FormValues) => {
    let paymentStarted = false;
    try {
      if (values.paymentMethod === PaymentMethodsEnum.Cash) {
        const receivedAmount = Number(cashReceivedAmount || 0);
        const hasCashReceivedAmount = cashReceivedAmount !== '';

        if (hasCashReceivedAmount && receivedAmount < Number(values.amountPaid || 0)) {
          showNotification({
            type: 'Error',
            title: t('Notifications:somethingWrong'),
            message: 'El efectivo recibido debe ser mayor o igual al monto a aplicar.'
          });
          return;
        }
      }

      paymentStarted = true;
      onPaymentPendingChange?.(true);
      await createAsync({
        orderId: order?._id || '',
        cashierBox: values.cashierBox || '',
        amountPaid: values.amountPaid,
        paymentMethod: values.paymentMethod,
        paymentDate: new Date().toISOString(),
        pinUserId: pinUser?._id || ''
      });
      close();
      onAddPaymentLine && (await onAddPaymentLine());
      showNotification({ type: 'Default', title: t('Notifications:success') });
      void autoOpenCashDrawerForPayments({
        cashDrawerConfig: store?.cashDrawerConfig,
        payments: [{ paymentMethod: values.paymentMethod, amountPaid: values.amountPaid }],
        source: 'payment'
      }).then((cashDrawerResult) => {
        if (cashDrawerResult.attempted && !cashDrawerResult.success) {
          showNotification({
            type: 'Error',
            title: t('Notifications:cashDrawerOpenFailedTitle'),
            message: cashDrawerResult.errorMessage || t('Notifications:cashDrawerOpenFailedBody')
          });
        }
      });
    } catch (e) {
      showNotification({ type: 'Error', title: t('Notifications:somethingWrong') });
    } finally {
      if (paymentStarted) onPaymentPendingChange?.(false);
    }
  };

  useEffect(() => {
    if (!isCashPayment) {
      setCashReceivedAmount('');
    }
  }, [isCashPayment]);

  useEffect(() => {
    if (cashierboxes) {
      const initialCashierBox = getInitialCashierBox(cashierboxes);
      const initialValues = {
        amountPaid: amountDue,
        paymentMethod: PaymentMethodsEnum.Card,
        cashierBox: initialCashierBox
      };
      form.setValues(initialValues);
      form.resetDirty(initialValues);
      setCashReceivedAmount('');
    }
  }, [cashierboxes, amountDue]);

  if (!order) return;

  return (
    <Modal
      data-testid="order-add-payment-line-modal"
      opened={opened}
      onClose={close}
      className={RemoveScroll.classNames.fullWidth}
      title={title}
      scrollAreaComponent={ScrollArea.Autosize}
    >
      <LoaderOverlay visible={isCreationPending} />
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          <Grid gutter="md">
            <Grid.Col>
              <LoaderOverlay visible={isGetCashierboxesLoading} />
            </Grid.Col>
            <PaymentCaptureFields
              cashierboxes={cashierboxes}
              cashierBoxInputProps={form.getInputProps('cashierBox')}
              paymentMethod={form.values.paymentMethod}
              onPaymentMethodChange={(value) =>
                form.setFieldValue('paymentMethod', value as PaymentMethodsEnum)
              }
              paymentMethodOptions={paymentMethodOptions}
              paymentMethodTestIdPrefix="order-add-payment-method"
              amountInputProps={form.getInputProps('amountPaid')}
              amountInputTestId="order-add-payment-line-amount"
              amountDue={amountDue}
              amountToApply={amountToApply}
              remainingBalance={remainingBalance}
              showCashReceived={isCashPayment}
              cashReceivedAmount={cashReceivedAmount}
              onCashReceivedAmountChange={setCashReceivedAmount}
              cashReceivedInputTestId="order-add-payment-line-cash-received"
              changeAmount={changeAmount}
            />
          </Grid>
          <Box
            bg="var(--mantine-color-body)"
            pt="sm"
            style={{
              position: 'sticky',
              bottom: 0,
              zIndex: 3,
              boxShadow: '0 -12px 20px var(--mantine-color-body)'
            }}
          >
            <Button
              data-testid="order-add-payment-line-submit"
              type="submit"
              fullWidth
              loading={isCreationPending}
            >
              {t('CommonButtons:submit')}
            </Button>
          </Box>
        </Stack>
      </form>
    </Modal>
  );
});
